import { Component } from '@theme/component';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { getIOSVersion, getRootUrl, translateSubtree } from '@theme/utilities';
import { replaceMatched, replaceOrInsert } from '@theme/libs-dom';

const DIALOG_ID = 'quick-add-dialog';
const MODAL_CONTENT_ID = 'quick-add-modal-content';
const SECTION_ID = 'fn-quick-add-form';

const SELECTORS = {
  CONTENT: '[data-quick-add-content]',
  VIEW_DETAILS: '[data-view-details]',
  GALLERY: '.quick-add-form__gallery',
  HEADER: '.quick-add-form__header',
  PRICE: '.quick-add-form__price-container',
  PICKER: '.quick-add-form__picker',
  FORM: 'product-form-component',
  SWATCH_RADIOS: 'product-linked input[type="radio"]',
  PRODUCT_TITLE: '[data-selector="product-title"]',
  PRODUCT_LINKED: 'product-linked',
  VARIANT_STATUS: '[data-selector="variant-status"]',
  BUTTON_SIZE_GUIDE: '[data-selector="size-guide-trigger"]',
  BUTTON_ADD_TO_CART: 'add-to-cart-component button[type="submit"]'
};

/**
 * Fetches a product's fn-quick-add-form section and returns the parsed content element.
 * @param {string} handle
 * @param {object} [options]
 * @param {string} [options.variantId]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Element | null>}
 */
async function fetchProductSection(handle, { variantId, signal } = {}) {
  const url = new URL(`${getRootUrl()}products/${handle}`, window.location.origin);
  url.searchParams.set('section_id', SECTION_ID);
  if (variantId) url.searchParams.set('variant', variantId);

  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const html = await response.text();
  return new DOMParser().parseFromString(html, 'text/html').querySelector(SELECTORS.CONTENT);
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  #cartAbortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#cartAbortController.abort();
  }

  /**
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const handle = this.dataset.productHandle;
    if (!handle) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.toggleAttribute('loading', true);

    try {
      const content = await fetchProductSection(handle, {
        variantId: this.dataset.productVariant,
        signal,
      });
      const modalContent = document.getElementById(MODAL_CONTENT_ID);
      if (!modalContent || !content) {
        this.toggleAttribute('loading', false);
        return;
      }

      // Translate while detached, then insert — no flash of source text.
      await translateSubtree(content);
      if (signal.aborted) return;

      modalContent.replaceChildren(content);
      window.tfRecalculate?.(); // init TrueFit widget before modal opens to avoid layout shift
      this.#openDialog();
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.toggleAttribute('loading', false);
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  };

  #openDialog() {
    const dialogEl = document.getElementById(DIALOG_ID);
    if (!(dialogEl instanceof DialogComponent)) return;

    this.toggleAttribute('loading', false);
    dialogEl.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { once: true });
    dialogEl.showDialog();
  }

  #handleCartUpdate = (/** @type {import('@theme/events').CartUpdateEvent} */ event) => {
    if (event.detail.data.didError) return;

    const dialogEl = document.getElementById(DIALOG_ID);
    if (dialogEl instanceof DialogComponent) dialogEl.closeDialog();
  };

  #handleDialogClose = () => {
    const modalContent = document.getElementById(MODAL_CONTENT_ID);
    if (modalContent) modalContent.replaceChildren();

    // iOS < 16.4 layout reflow bug: grid collapses after dialog close.
    // Two rAF frames: first to read layout, second to clear the forced width.
    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4))
      return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (!grid) return;

      const currentWidth = grid.getBoundingClientRect().width;
      grid.style.width = `${currentWidth - 1}px`;
      requestAnimationFrame(() => {
        grid.style.width = '';
      });
    });
  };
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

/**
 * Intercepts `linked-color:change` from child swatches to swap the product
 * via Section Rendering API, and `variant:update` to sync the view-details link.
 * Preserves swatch order by surgically replacing everything except the linked section.
 */
class QuickAddDialog extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.addEventListener(ThemeEvents.linkedColorChange, this.#handleLinkedColorChange);
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener(ThemeEvents.linkedColorChange, this.#handleLinkedColorChange);
    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#abortController?.abort();
  }

  /** @param {import('@theme/events').VariantUpdateEvent} event */
  #handleVariantUpdate = (event) => {
    const variantId = event.detail?.resource?.id;
    if (!variantId) return;

    /** @type {HTMLAnchorElement | null} */
    const linkEl = this.querySelector(SELECTORS.VIEW_DETAILS);
    if (!linkEl) return;

    const url = new URL(linkEl.href);
    url.searchParams.set('variant', variantId);
    linkEl.href = url.toString();
  };

  /** @param {CustomEvent} event */
  #handleLinkedColorChange = async (event) => {
    const handle = event.detail?.productHandle ?? '';
    if (!handle) return;

    const productId = event.detail?.productId ?? '';
    if (productId) {
      /** @type {NodeListOf<HTMLInputElement>} */
      const radios = this.querySelectorAll(SELECTORS.SWATCH_RADIOS);
      radios.forEach((radio) => {
        radio.checked = radio.dataset.productId === productId;
      });
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    try {
      const newForm = await fetchProductSection(handle, { signal });
      const currentForm = document
        .getElementById(MODAL_CONTENT_ID)
        ?.querySelector(SELECTORS.CONTENT);
      if (!newForm || !currentForm) return;

      // Translate newForm while detached (not currentForm — its swatches are already
      // translated and preserved) so the swap injects translated nodes, no flash.
      await translateSubtree(newForm);
      if (signal.aborted) return;

      replaceMatched(currentForm, newForm, SELECTORS.GALLERY);
      replaceMatched(currentForm, newForm, SELECTORS.HEADER);
      replaceMatched(currentForm, newForm, SELECTORS.PRICE);
      replaceOrInsert(currentForm, newForm, SELECTORS.PICKER, SELECTORS.FORM);
      replaceMatched(currentForm, newForm, SELECTORS.FORM);
      window.tfRecalculate?.(); // re-init TrueFit widget after linked color swap
    } catch (error) {
      if (error.name !== 'AbortError') throw error;
    } finally {
      this.#abortController = null;
    }
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
